import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Descriptions,
  Alert,
  Space,
  Typography,
  Tag,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { usePayments } from "../../../hooks/finance/usePayments";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Text } = Typography;

const PURPOSE_OPTIONS = [
  { label: "Tuition Fees", value: "Tuition Fees" },
  { label: "Custom Fee", value: "Custom Fee" },
  { label: "Transport Fees", value: "Transport Fees" },
  { label: "BOT Clearance", value: "BOT Clearance" },
  { label: "Admission Fees", value: "Admission Fees" },
];

const PaymentValidationModal = ({ visible, onCancel, onSuccess, payment }) => {
  const [form] = Form.useForm();
  const { validatePayment } = usePayments();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && payment) {
      form.setFieldsValue({
        purpose: payment.purpose || undefined,
        correctCode: payment.paymentCode || undefined,
        notes: undefined,
      });
    } else {
      form.resetFields();
    }
  }, [visible, payment, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Build payload
      const validationData = {};
      if (values.purpose) validationData.purpose = values.purpose;
      if (values.correctCode) validationData.correctCode = values.correctCode;
      if (values.notes) validationData.notes = values.notes;

      const result = await validatePayment(payment.paymentId, validationData);

      if (result) {
        message.success("Payment validated successfully");
        form.resetFields();
        onSuccess();
      }
    } catch (error) {
      // Form validation error - antd shows error messages
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const isSuspense = payment?.status === "Suspense";
  const verifiedStudentName = payment?.studentName || payment?.student?.name;
  const reportedStudentName = payment?.reportedStudentName;

  return (
    <Modal
      title={
        <Space>
          <SafetyOutlined
            style={{ color: isSuspense ? "#ff4d4f" : "#1890ff" }}
          />
          <span>Validate Payment</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText="Validate"
      okButtonProps={{ loading: submitting, icon: <CheckCircleOutlined /> }}
      width={500}
      destroyOnClose
    >
      {payment && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {/* Payment summary */}
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Reference" span={2}>
              <Text copyable style={{ fontSize: 12 }}>
                {payment.paymentReference}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Student">
              <Space direction="vertical" size={2}>
                <span>
                  {verifiedStudentName || reportedStudentName || (
                    <Text type="secondary">Unknown</Text>
                  )}
                  {!verifiedStudentName && reportedStudentName && (
                    <Tag color="orange" style={{ marginLeft: 6 }}>
                      Unmatched · SurePay provided
                    </Tag>
                  )}
                </span>
                {!verifiedStudentName && payment.surePayStudentCode && (
                  <Text type="secondary">
                    SurePay code: {payment.surePayStudentCode}
                  </Text>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong>UGX {formatCurrency(payment.amount)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Method">
              {payment.paymentMethod || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {payment.paymentDate
                ? dayjs(payment.paymentDate).format("DD MMM YYYY")
                : "—"}
            </Descriptions.Item>
          </Descriptions>

          {/* Context alerts */}
          {!isSuspense && (
            <Alert
              type="info"
              showIcon
              message="This payment looks valid. Confirm to allocate it, or update the code/purpose below if needed."
            />
          )}
          {isSuspense && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="Payment in Suspense"
              description="Provide the correct purpose and payment code so the system can allocate this payment properly."
            />
          )}

          {/* Validation form */}
          <Form form={form} layout="vertical" size="small">
            <Form.Item
              label="Purpose"
              name="purpose"
              rules={
                isSuspense
                  ? [
                      {
                        required: true,
                        message: "Please select the payment purpose",
                      },
                    ]
                  : []
              }
            >
              <Select
                placeholder="Select payment purpose"
                options={PURPOSE_OPTIONS}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="Correct Payment Code"
              name="correctCode"
              extra={
                isSuspense
                  ? "Enter the correct SPC, TPC, BOTPC, CFPC, or APC for this payment."
                  : "Leave blank to use the existing code."
              }
            >
              <Input placeholder="e.g. SPC12345 or TPC98765" />
            </Form.Item>
            <Form.Item label="Notes" name="notes">
              <TextArea rows={2} placeholder="Optional validation notes" />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  );
};

export default PaymentValidationModal;
